/**
 * Common DOM operations shared across content conversion methods.
 * Eliminates duplication of image placeholder, S3 link removal, and lexindex handling.
 */

/**
 * Removes all <lexindex> elements from the document
 */
export const removeLexindexTags = (doc: Document): void => {
  doc.querySelectorAll('lexindex').forEach((el) => el.remove());
};

/**
 * Processes images with data-attachment-id:
 * - Clears src (images loaded separately by the application)
 * - Adds data-image-placeholder attribute if not present
 * - Optionally preserves width/height attributes
 */
export const processAttachmentImages = (doc: Document, preserveDimensions = false): void => {
  doc.querySelectorAll('img[data-attachment-id]').forEach((img) => {
    if (preserveDimensions) {
      // Re-set dimensions to ensure they're preserved after other transformations
      const width = img.getAttribute('width');
      const height = img.getAttribute('height');
      if (width) img.setAttribute('width', width);
      if (height) img.setAttribute('height', height);
    }

    img.setAttribute('src', '');

    if (!img.hasAttribute('data-image-placeholder')) {
      img.setAttribute('data-image-placeholder', 'image');
    }
  });
};

/**
 * Removes anchor tags that reference S3 or have attachment IDs:
 * - <a data-attachment-id="...">
 * - <a href="s3://...">
 */
export const removeS3AttachmentLinks = (doc: Document): void => {
  doc.querySelectorAll('a').forEach((a) => {
    if (a.hasAttribute('data-attachment-id') || a.getAttribute('href')?.startsWith('s3://')) {
      a.remove();
    }
  });
};

/**
 * Applies all common cleanup operations to a document.
 * Combines: lexindex removal, image placeholder handling, S3 link removal
 */
export const applyCommonCleanup = (doc: Document, preserveImageDimensions = false): void => {
  removeLexindexTags(doc);
  processAttachmentImages(doc, preserveImageDimensions);
  removeS3AttachmentLinks(doc);
};

/**
 * Removes leading empty paragraph tags from HTML content.
 * Matches: <p><br></p> or <p class="..."><br /></p>
 */
export const removeLeadingEmptyParagraph = (html: string): string => {
  return html.replace(/^(\s*<p(?:\s+class="[^"]*")?\s*>\s*<br\s*\/?>\s*<\/p>\s*)/i, '');
};

/**
 * List of classes that should be preserved during class filtering
 */
export const PRESERVED_CLASS_PREFIXES = ['PhxEditorTheme__', 'phx-'];

export const PRESERVED_CLASS_NAMES = [
  'text-entity',
  'phx-cross-ref-link',
  'editor-delete',
  'editor-insert',
  'editor-delete-disabled',
  'editor-insert-disabled',
];

/**
 * Checks if a class should be preserved during filtering
 */
export const shouldPreserveClass = (className: string): boolean => {
  return (
    PRESERVED_CLASS_PREFIXES.some((prefix) => className.startsWith(prefix)) || PRESERVED_CLASS_NAMES.includes(className)
  );
};
